import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Download, Trash2, RefreshCw, HardDrive,
  Database, FolderArchive, Clock, Calendar, Plus, Loader2,
  Settings2, ToggleLeft, ToggleRight, AlertCircle, CheckCircle,
  FileArchive, Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { backupsAPI } from '../api';
import { toast } from '../hooks/use-toast';

const ConfiguracionBackups = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState({
    auto_backup_enabled: false,
    backup_hour: 3,
    retention_days: 30,
    include_uploads: true
  });
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [backupsRes, statsRes, configRes] = await Promise.all([
        backupsAPI.list(),
        backupsAPI.getStats(),
        backupsAPI.getConfig()
      ]);
      setBackups(backupsRes.data || []);
      setStats(statsRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos de backups",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateBackup = async (includeUploads = true) => {
    try {
      setCreating(true);
      const response = await backupsAPI.create(includeUploads);
      toast({
        title: "Backup creado",
        description: response.data.message,
      });
      await loadData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo crear el backup",
        variant: "destructive"
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadBackup = async (backup) => {
    try {
      const response = await backupsAPI.download(backup.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', backup.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({
        title: "Descarga iniciada",
        description: `Descargando ${backup.filename}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo descargar el backup",
        variant: "destructive"
      });
    }
  };

  const handleDeleteBackup = async () => {
    if (!selectedBackup) return;
    try {
      await backupsAPI.delete(selectedBackup.id);
      toast({
        title: "Backup eliminado",
        description: "La copia de seguridad ha sido eliminada",
      });
      setIsDeleteDialogOpen(false);
      setSelectedBackup(null);
      await loadData();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el backup",
        variant: "destructive"
      });
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSavingConfig(true);
      await backupsAPI.updateConfig(config);
      toast({
        title: "Configuración guardada",
        description: "Los ajustes de backups automáticos han sido actualizados",
      });
      setIsConfigDialogOpen(false);
      await loadData();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo guardar la configuración",
        variant: "destructive"
      });
    } finally {
      setSavingConfig(false);
    }
  };

  const handleToggleAuto = async () => {
    try {
      const response = await backupsAPI.toggleAuto();
      toast({
        title: response.data.auto_backup_enabled ? "Backups automáticos activados" : "Backups automáticos desactivados",
        description: response.data.message,
      });
      await loadData();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo cambiar el estado",
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="text-gray-600">Cargando copias de seguridad...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/configuracion')}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              Copias de Seguridad
            </h1>
            <p className="text-gray-600">Gestiona las copias de seguridad de la base de datos y archivos</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
          <Button variant="outline" onClick={() => setIsConfigDialogOpen(true)}>
            <Settings2 className="w-4 h-4 mr-2" />
            Configurar
          </Button>
          <Button onClick={() => handleCreateBackup(true)} disabled={creating}>
            {creating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Crear Backup
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileArchive className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Backups</p>
                <p className="text-2xl font-bold">{stats?.total_backups || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <HardDrive className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Espacio Usado</p>
                <p className="text-2xl font-bold">{stats?.total_size_human || '0 B'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Base de Datos</p>
                <p className="text-2xl font-bold">{stats?.database_size_human || '0 B'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <FolderArchive className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Archivos (Uploads)</p>
                <p className="text-2xl font-bold">{stats?.uploads_size_human || '0 B'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Auto Backup Status Card */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${config.auto_backup_enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                <Clock className={`w-6 h-6 ${config.auto_backup_enabled ? 'text-green-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Backups Automáticos</h3>
                <p className="text-sm text-gray-500">
                  {config.auto_backup_enabled 
                    ? `Programados diariamente a las ${String(config.backup_hour).padStart(2, '0')}:00h`
                    : 'Desactivados - Los backups deben crearse manualmente'}
                </p>
                {stats?.last_auto_backup && (
                  <p className="text-xs text-gray-400 mt-1">
                    Último backup automático: {formatDate(stats.last_auto_backup)}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={config.auto_backup_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                {config.auto_backup_enabled ? 'Activo' : 'Inactivo'}
              </Badge>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleToggleAuto}
              >
                {config.auto_backup_enabled ? (
                  <ToggleRight className="w-5 h-5 text-green-600" />
                ) : (
                  <ToggleLeft className="w-5 h-5 text-gray-400" />
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Backups List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileArchive className="w-5 h-5" />
            Lista de Copias de Seguridad
          </CardTitle>
          <CardDescription>
            {backups.length} {backups.length === 1 ? 'copia disponible' : 'copias disponibles'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {backups.length === 0 ? (
            <div className="text-center py-12">
              <FileArchive className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay copias de seguridad</h3>
              <p className="text-gray-500 mb-4">Crea tu primera copia de seguridad para proteger tus datos</p>
              <Button onClick={() => handleCreateBackup(true)} disabled={creating}>
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Crear Primera Copia
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tamaño</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contenido</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Creado por</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {backups.map((backup) => (
                    <tr key={backup.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileArchive className="w-4 h-4 text-blue-500" />
                          <span className="font-medium text-sm">{backup.filename}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatDate(backup.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline">{backup.size_human}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Badge className="bg-purple-100 text-purple-700 text-xs">BD</Badge>
                          {backup.includes_uploads && (
                            <Badge className="bg-orange-100 text-orange-700 text-xs">Archivos</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {backup.created_by || 'Desconocido'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadBackup(backup)}
                            title="Descargar"
                          >
                            <Download className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedBackup(backup);
                              setIsDeleteDialogOpen(true);
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900">Información sobre las copias de seguridad</h4>
              <ul className="text-sm text-blue-800 mt-2 space-y-1 list-disc list-inside">
                <li>Las copias incluyen la base de datos SQLite completa</li>
                <li>Opcionalmente pueden incluir todos los archivos adjuntos (uploads)</li>
                <li>Se recomienda descargar las copias y guardarlas en un lugar seguro externo</li>
                <li>Los backups automáticos se ejecutan diariamente a la hora configurada</li>
                <li>Retención: Los backups más antiguos de {config.retention_days} días se eliminan automáticamente</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Config Dialog */}
      <Dialog open={isConfigDialogOpen} onOpenChange={setIsConfigDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              Configuración de Backups Automáticos
            </DialogTitle>
            <DialogDescription>
              Configura cuándo y cómo se realizan las copias de seguridad automáticas
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Backups Automáticos</Label>
                <p className="text-sm text-gray-500">Ejecutar backups diariamente</p>
              </div>
              <Switch
                checked={config.auto_backup_enabled}
                onCheckedChange={(checked) => setConfig({...config, auto_backup_enabled: checked})}
              />
            </div>

            <div className="space-y-2">
              <Label>Hora del Backup</Label>
              <Select 
                value={String(config.backup_hour)} 
                onValueChange={(value) => setConfig({...config, backup_hour: parseInt(value)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({length: 24}, (_, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {String(i).padStart(2, '0')}:00h
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Se recomienda una hora de baja actividad</p>
            </div>

            <div className="space-y-2">
              <Label>Días de Retención</Label>
              <Select 
                value={String(config.retention_days)} 
                onValueChange={(value) => setConfig({...config, retention_days: parseInt(value)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 días</SelectItem>
                  <SelectItem value="14">14 días</SelectItem>
                  <SelectItem value="30">30 días</SelectItem>
                  <SelectItem value="60">60 días</SelectItem>
                  <SelectItem value="90">90 días</SelectItem>
                  <SelectItem value="180">180 días</SelectItem>
                  <SelectItem value="365">1 año</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Los backups más antiguos se eliminarán automáticamente</p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base">Incluir Archivos</Label>
                <p className="text-sm text-gray-500">Añadir carpeta uploads al backup</p>
              </div>
              <Switch
                checked={config.include_uploads}
                onCheckedChange={(checked) => setConfig({...config, include_uploads: checked})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConfigDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveConfig} disabled={savingConfig}>
              {savingConfig ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar copia de seguridad?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el archivo 
              <strong className="mx-1">{selectedBackup?.filename}</strong>
              del servidor.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteBackup}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ConfiguracionBackups;
